import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { submitVendorApplication } from '../api/api';
import { uploadMultipleImages } from '../utils/firebaseStorage';
import { trackVendorApplication } from '../utils/firebaseAnalytics';
import './VendorApplication.css';

function VendorApplication() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    description: '',
    location: { city: '', province: '' },
    products: [{ name: '', description: '', price: '', category: '', culture: '', images: [], imageUrls: [] }]
  });
  const [policyAgreements, setPolicyAgreements] = useState({
    termsConditions: false,
    privacyPolicy: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('location.')) {
      const field = name.split('.')[1];
      setFormData({ ...formData, location: { ...formData.location, [field]: value } });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleProductChange = (index, field, value) => {
    const newProducts = [...formData.products];
    newProducts[index][field] = value;
    setFormData({ ...formData, products: newProducts });
  };

  const validateImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        URL.revokeObjectURL(url);
        
        if (width < 1200 || height < 1200) {
          resolve({
            file: file.name,
            warning: `Low resolution: ${width}x${height}px. Recommended minimum: 1200x1200px`
          });
        } else {
          resolve(null);
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      
      img.src = url;
    });
  };

  const handleImageChange = async (index, files) => {
    const newProducts = [...formData.products];
    newProducts[index].images = Array.from(files);
    setFormData({ ...formData, products: newProducts });
    
    // Validate images locally first
    const warnings = [];
    for (const file of Array.from(files)) {
      const warning = await validateImage(file);
      if (warning) {
        warnings.push({ productIndex: index, ...warning });
      }
    }
    
    if (warnings.length > 0) {
      const warningMessages = warnings.map(w => `- ${w.file}: ${w.warning}`).join('\n');
      alert(
        `⚠️ IMAGE QUALITY WARNING\n\n` +
        `Some images do not meet minimum resolution requirements:\n\n${warningMessages}\n\n` +
        `These images can still be submitted, but we recommend using higher quality images for better results.\n\n` +
        `See the Image Requirements page for full guidelines.`
      );
    }
    
    // Automatically upload to Firebase
    if (files.length > 0) {
      await uploadProductImages(index, Array.from(files));
    }
  };

  const uploadProductImages = async (productIndex, files) => {
    try {
      setUploadProgress(prev => ({ ...prev, [productIndex]: 0 }));
      setUploadErrors(prev => ({ ...prev, [productIndex]: null }));

      // Firebase Storage path for this product
      const storagePath = `temp/${formData.businessName || 'pending'}/${productIndex}/`;

      // Upload images to Firebase Storage
      const uploadedUrls = await uploadMultipleImages(files, storagePath, (progress) => {
        setUploadProgress(prev => ({
          ...prev,
          [productIndex]: progress.percentage
        }));
      });

      // Update form with Firebase URLs
      const newProducts = [...formData.products];
      newProducts[productIndex].imageUrls = uploadedUrls;
      setFormData({ ...formData, products: newProducts });

      // Log analytics
      trackVendorApplication(formData.businessName, formData.products.length);
    } catch (error) {
      console.error('Image upload error:', error);
      setUploadErrors(prev => ({
        ...prev,
        [productIndex]: error.message || 'Failed to upload images'
      }));
    }
  };

  const addProduct = () => {
    setFormData({
      ...formData,
      products: [...formData.products, { name: '', description: '', price: '', category: '', culture: '', images: [] }]
    });
  };

  const removeProduct = (index) => {
    const newProducts = formData.products.filter((_, i) => i !== index);
    setFormData({ ...formData, products: newProducts });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if required policies are accepted (Terms & Conditions and POPIA/Privacy Policy)
    if (!policyAgreements.termsConditions || !policyAgreements.privacyPolicy) {
      alert('⚠️ Please accept the Terms & Conditions and Privacy Policy (POPIA) before submitting your application.');
      return;
    }

    // Check if all required images are uploaded
    const hasAllImagesUploaded = formData.products.every(p => p.imageUrls && p.imageUrls.length > 0);
    if (!hasAllImagesUploaded) {
      alert('⚠️ Please wait for all images to finish uploading before submitting your application.');
      return;
    }

    setSubmitting(true);

    try {
      // Prepare data with Firebase image URLs instead of files
      // Send as plain JSON (images are already on Firebase Storage)
      const dataToSend = {
        ...formData,
        policyAgreements: policyAgreements,
        agreementDate: new Date().toISOString(),
        products: formData.products.map(p => ({ 
          ...p, 
          images: [], // No image files - they're on Firebase
          imageUrls: p.imageUrls || [] // Include Firebase URLs
        }))
      };

      const response = await submitVendorApplication(dataToSend);
      setReferenceNumber(response.data.application?.applicationNumber || response.data.referenceNumber || 'APPLICATION-' + Date.now());
      setSubmitted(true);

      // Track successful submission
      trackVendorApplication(formData.businessName, formData.products.length);
    } catch (error) {
      console.error('=== VENDOR APPLICATION ERROR ===');
      console.error('Full error:', error);
      console.error('Error response:', error.response);
      console.error('Error request:', error.request);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Something went wrong! ';
      
      if (error.response) {
        // Server responded with error
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request made but no response
        console.error('No response received');
        errorMessage = 'No response from server. Please check your internet connection.';
      } else {
        // Error setting up request
        console.error('Request setup error');
        errorMessage = error.message || 'Failed to submit application';
      }
      
      alert('Error submitting application: ' + errorMessage + '\n\nPlease contact support at mashaba.enterprise2025@gmail.com with this information:\n' + JSON.stringify(error.response?.data || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="vendor-application container">
        <div className="success-message">
          <h1>✅ Application Submitted!</h1>
          <p><strong>Your Reference Number:</strong> <span style={{ fontSize: '1.5em', color: '#3498db' }}>{referenceNumber}</span></p>
          <p>Please keep this reference number for your records.</p>
          <p>Thank you for your application. We will review it and get back to you within 3-5 business days.</p>
          <p>A confirmation email has been sent to <strong>mashaba.enterprise2025@gmail.com</strong></p>
        </div>
      </div>
    );
  }

  return (
    <div className="vendor-application container">
      <h1>Become a Vendor</h1>
      <p className="intro">Join our platform and share your authentic South African cultural products with the world.</p>
      
      <div className="image-requirements-banner">
        <h3>📸 Important: Product Image Requirements</h3>
        <p>Please read our <Link to="/image-requirements" target="_blank" className="requirements-link">Image Quality Guidelines</Link> before uploading product photos. While you can submit images of any quality, following these guidelines will help your products sell better!</p>
      </div>

      <form onSubmit={handleSubmit} className="vendor-form">
        <div className="form-section">
          <h2>Personal Information</h2>
          <input type="text" name="name" placeholder="Full Name" value={formData.name} onChange={handleInputChange} required />
          <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleInputChange} required />
          <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleInputChange} required />
        </div>

        <div className="form-section">
          <h2>Business Information</h2>
          <input type="text" name="businessName" placeholder="Business Name" value={formData.businessName} onChange={handleInputChange} required />
          <textarea name="description" placeholder="Business Description" value={formData.description} onChange={handleInputChange} rows="4" required />
          <input type="text" name="location.city" placeholder="City" value={formData.location.city} onChange={handleInputChange} required />
          <input type="text" name="location.province" placeholder="Province" value={formData.location.province} onChange={handleInputChange} required />
        </div>

        <div className="form-section">
          <h2>Products</h2>
          {formData.products.map((product, index) => (
            <div key={index} className="product-entry">
              <h3>Product {index + 1}</h3>
              <input type="text" placeholder="Product Name" value={product.name} onChange={(e) => handleProductChange(index, 'name', e.target.value)} required />
              <textarea placeholder="Description" value={product.description} onChange={(e) => handleProductChange(index, 'description', e.target.value)} rows="3" />
              <input type="number" placeholder="Price (ZAR)" value={product.price} onChange={(e) => handleProductChange(index, 'price', e.target.value)} required min="0" step="0.01" />
              
              <select value={product.category} onChange={(e) => handleProductChange(index, 'category', e.target.value)} required>
                <option value="">Select Category</option>
                <option value="Traditional Attire">Traditional Attire</option>
                <option value="Accessories">Accessories</option>
                <option value="Jewelry">Jewelry</option>
                <option value="Headwear">Headwear</option>
                <option value="Footwear">Footwear</option>
                <option value="Home Decor">Home Decor</option>
                <option value="Other">Other</option>
              </select>

              <select value={product.culture} onChange={(e) => handleProductChange(index, 'culture', e.target.value)} required>
                <option value="">Select Culture</option>
                <option value="Zulu">Zulu</option>
                <option value="Xhosa">Xhosa</option>
                <option value="Ndebele">Ndebele</option>
                <option value="Sotho">Sotho</option>
                <option value="Tswana">Tswana</option>
                <option value="Venda">Venda</option>
                <option value="Tsonga">Tsonga</option>
                <option value="Swati">Swati</option>
                <option value="English">English</option>
                <option value="Afrikaans">Afrikaans</option>
                <option value="Coloured">Coloured</option>
                <option value="Mixed">Mixed</option>
                <option value="Other">Other (specify in description)</option>
              </select>

              <div className="file-input">
                <label>Product Images (up to 5) <Link to="/image-requirements" target="_blank" className="inline-requirements-link">(View Image Requirements)</Link></label>
                <input type="file" accept="image/*" multiple onChange={(e) => handleImageChange(index, e.target.files)} required />
                <small className="image-hint">💡 Recommended: 2000x2000px or higher, under 10MB each</small>
                
                {/* Upload Progress */}
                {uploadProgress[index] !== undefined && uploadProgress[index] < 100 && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${uploadProgress[index]}%` }}>
                        {uploadProgress[index]}%
                      </div>
                    </div>
                    <p className="upload-status">⏳ Uploading images to Firebase Storage...</p>
                  </div>
                )}
                
                {/* Upload Success */}
                {formData.products[index].imageUrls && formData.products[index].imageUrls.length > 0 && (
                  <div className="upload-success">
                    <p>✅ {formData.products[index].imageUrls.length} image(s) uploaded successfully</p>
                  </div>
                )}
                
                {/* Upload Error */}
                {uploadErrors[index] && (
                  <div className="upload-error">
                    <p>❌ Upload failed: {uploadErrors[index]}</p>
                  </div>
                )}
              </div>

              {formData.products.length > 1 && (
                <button type="button" onClick={() => removeProduct(index)} className="remove-product-btn">
                  Remove Product
                </button>
              )}
            </div>
          ))}
          
          <button type="button" onClick={addProduct} className="add-product-btn">
            + Add Another Product
          </button>
        </div>

        <div className="form-section policy-agreements">
          <h2>📋 Policy Acceptance</h2>
          <p className="policy-intro">
            Before submitting your vendor application, you must accept our Terms & Conditions and Privacy Policy (POPIA).
          </p>
          
          <div className="policy-checkbox-group">
            <label className="policy-checkbox">
              <input 
                type="checkbox" 
                checked={policyAgreements.termsConditions}
                onChange={(e) => setPolicyAgreements({...policyAgreements, termsConditions: e.target.checked})}
              />
              <span>
                I agree to the <Link to="/terms-conditions" target="_blank" className="policy-link">Terms & Conditions</Link>
              </span>
            </label>
            {!policyAgreements.termsConditions && <div className="policy-error">✗ Required</div>}
          </div>

          <div className="policy-checkbox-group">
            <label className="policy-checkbox">
              <input 
                type="checkbox" 
                checked={policyAgreements.privacyPolicy}
                onChange={(e) => setPolicyAgreements({...policyAgreements, privacyPolicy: e.target.checked})}
              />
              <span>
                I have read and accept the <Link to="/privacy-policy" target="_blank" className="policy-link">Privacy Policy & POPIA Compliance</Link>
              </span>
            </label>
            {!policyAgreements.privacyPolicy && <div className="policy-error">✗ Required</div>}
          </div>

          <div className="popia-statement">
            <strong>🔒 POPIA Compliance:</strong> By submitting this application, you acknowledge that you have read our Privacy Policy 
            and agree to our collection and processing of your personal information in accordance with the Protection of Personal Information Act (POPIA).
          </div>
        </div>

        <button type="submit" disabled={submitting} className="submit-btn">
          {submitting ? 'Submitting...' : 'Submit Application'}
        </button>
      </form>
    </div>
  );
}

export default VendorApplication;
